import React from "react";
import { Button, Form, Input, Select, Tooltip } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
} from "@ant-design/icons";

interface CodeOption {
  value: string;
  label?: string;
}

interface CategoryOption {
  value: string;
  label: string;
}

interface Props {
  templateName: string;
  templateCode: string;
  category?: string | null;
  subject: string;
  onTemplateNameChange: (v: string) => void;
  onTemplateCodeChange: (v: string) => void;
  onCategoryChange: (v: string | null) => void;
  onSubjectChange: (v: string) => void;
  templateCodeOptions?: CodeOption[];
  categoryOptions?: CategoryOption[];
  codeChecking?: boolean;
  codeAvailable?: boolean | null;
  codeMessage?: string | null;
  onCheckCode?: () => void;
  isEditing?: boolean;
}

const TemplateFormBar: React.FC<Props> = ({
  templateName,
  templateCode,
  category,
  subject,
  onTemplateNameChange,
  onTemplateCodeChange,
  onCategoryChange,
  onSubjectChange,
  templateCodeOptions,
  categoryOptions,
  codeChecking,
  codeAvailable,
  codeMessage,
  onCheckCode,
  isEditing,
}) => {
  const hasCodeOptions =
    Array.isArray(templateCodeOptions) && templateCodeOptions.length > 0;
  const hasCategoryOptions =
    Array.isArray(categoryOptions) && categoryOptions.length > 0;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: hasCategoryOptions
          ? "1fr 1fr 1fr 2fr"
          : "1fr 1fr 2fr",
        gap: "0 16px",
        flexShrink: 0,
      }}
    >
      <Form.Item label="Template Name" required style={{ marginBottom: 10 }}>
        <Input
          placeholder="e.g. Monthly Payroll Notice"
          value={templateName}
          onChange={(e) => onTemplateNameChange(e.target.value)}
        />
      </Form.Item>
      <Form.Item
        label="Template Code"
        required
        style={{ marginBottom: 10 }}
        validateStatus={
          codeAvailable === false
            ? "error"
            : codeAvailable === true
              ? "success"
              : undefined
        }
        help={codeMessage || undefined}
      >
        {hasCodeOptions ? (
          <Select
            placeholder="Select template code..."
            value={templateCode || undefined}
            onChange={(val) => onTemplateCodeChange(val)}
            options={templateCodeOptions!.map((t) => ({
              value: t.value,
              label: t.label || t.value,
            }))}
          />
        ) : (
          <Input
            placeholder="e.g. payroll_notice_monthly"
            value={templateCode}
            onChange={(e) => onTemplateCodeChange(e.target.value)}
            disabled={Boolean(isEditing)}
            suffix={
              isEditing ? null : (
                <Tooltip title="Check availability">
                  <Button
                    size="small"
                    type="text"
                    icon={
                      codeChecking ? (
                        <LoadingOutlined />
                      ) : codeAvailable === true ? (
                        <CheckCircleFilled style={{ color: "#22c55e" }} />
                      ) : codeAvailable === false ? (
                        <CloseCircleFilled style={{ color: "#ef4444" }} />
                      ) : null
                    }
                    onClick={onCheckCode}
                    disabled={codeChecking || !templateCode.trim()}
                  >
                    Check
                  </Button>
                </Tooltip>
              )
            }
          />
        )}
      </Form.Item>
      {hasCategoryOptions ? (
        <Form.Item label="Category" style={{ marginBottom: 10 }}>
          <Select
            placeholder="Select category..."
            value={category || undefined}
            onChange={(val) => onCategoryChange(val || null)}
            allowClear
            options={categoryOptions}
          />
        </Form.Item>
      ) : null}
      <Form.Item label="Subject Line" required style={{ marginBottom: 10 }}>
        <Input
          placeholder="e.g. Your Application for {{job_title}}"
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
        />
      </Form.Item>
    </div>
  );
};

export default TemplateFormBar;
